import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Login.module.css';
import tatuLogo from '../../assets/tatuchat-logo.png';

/* ====== Ícones SVG inline ====== */
function Icon({ name, size = 18 }) {
  const s = {
    width: size, height: size, strokeWidth: 1.8,
    fill: 'none', stroke: 'currentColor',
    strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  switch (name) {
    case 'mail':
      return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
    case 'lock':
      return <svg viewBox="0 0 24 24" {...s}><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>;
    case 'eye':
      return <svg viewBox="0 0 24 24" {...s}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
    case 'eye-off':
      return <svg viewBox="0 0 24 24" {...s}><path d="M3 3l18 18" /><path d="M10.6 6.1A10.4 10.4 0 0112 6c6.5 0 10 7 10 7a17 17 0 01-3.5 4.4M6.6 7.5A17 17 0 002 12s3.5 7 10 7c1.5 0 2.9-.3 4.1-.8" /><path d="M9.9 9.9a3 3 0 004.2 4.2" /></svg>;
    case 'arrow':
      return <svg viewBox="0 0 24 24" {...s}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'check':
      return <svg viewBox="0 0 24 24" {...s}><path d="M5 12l4 4 10-10" /></svg>;
    default:
      return null;
  }
}

/* ====== Campo com label fixo acima + placeholder de exemplo ====== */
function FormField({ label, type, value, onChange, error, icon, autoComplete, name, placeholder, rightSlot, disabled }) {
  const [focused, setFocused] = useState(false);

  const fieldClasses = [
    styles.field,
    focused && styles.fieldFocused,
    error && styles.fieldError,
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.fieldGroup}>
      <label className={styles.fieldLabel} htmlFor={name}>{label}</label>
      <div className={fieldClasses}>
        <span className={styles.fieldIcon}>{icon}</span>
        <input
          id={name}
          type={type}
          value={value}
          name={name}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={styles.fieldInput}
          disabled={disabled}
        />
        {rightSlot && <span className={styles.fieldRight}>{rightSlot}</span>}
      </div>
      {error && <span className={styles.fieldErrorText}>{error}</span>}
    </div>
  );
}

/* ====== Tela de sucesso pós-login ====== */
function SuccessState({ email }) {
  return (
    <div className={styles.success}>
      <div className={styles.successRing}>
        <div className={styles.successCircle}>
          <Icon name="check" size={28} />
        </div>
      </div>
      <h3>Tudo certo!</h3>
      <p>
        Bem-vindo de volta{email ? `, ${email.split('@')[0]}` : ''}. Estamos preparando seu painel.
      </p>
      <div className={styles.loaderBar}>
        <span className={styles.loaderBarInner} />
      </div>
    </div>
  );
}

/* ====== Componente Principal do Login ====== */
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [formError, setFormError] = useState('');
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  function validate() {
    const e = {};
    if (!email) e.email = 'Informe seu e-mail';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'E-mail inválido';
    if (!password) e.password = 'Informe sua senha';
    else if (password.length < 4) e.password = 'Mínimo 4 caracteres';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setFormError('');
    if (!validate()) return;
    setStatus('loading');

    const result = await login(email, password);

    if (result.success) {
      setStatus('success');
      // Aguardar a animação de sucesso e redirecionar
      setTimeout(() => {
        if (result.deveTrocarSenha) {
          navigate('/trocar-senha');
        } else {
          navigate('/');
        }
      }, 1800);
    } else {
      setStatus('error');
      setFormError(result.message || 'E-mail ou senha incorretos. Tente novamente.');
      setTimeout(() => setStatus('idle'), 400);
    }
  }

  return (
    <div className={styles.stage}>
      <div className={styles.bgGlow} aria-hidden="true" />

      {/* Painel lateral da marca (apenas desktop) */}
      <aside className={styles.brandPane}>
        <div className={styles.brandInner}>
          <div className={styles.brandMark}>
            <img src={tatuLogo} alt="TATUDashboard" />
          </div>
          <p className={styles.brandKicker}>Bem-vindo ao</p>
          <h1 className={styles.brandTitle}>
            <span className={styles.brandTitleAccent}>TATU</span>
            <span className={styles.brandTitleWarm}>Dashboard</span>
          </h1>
          <p className={styles.brandSub}>
            Gestão de produtos, vendas e atendimento em um só lugar.
          </p>
          <ul className={styles.brandFeatures}>
            <li><span className={styles.featureDot} />PDV integrado</li>
            <li><span className={styles.featureDot} />Atendimento WhatsApp</li>
            <li><span className={styles.featureDot} />Relatórios em tempo real</li>
          </ul>
        </div>
      </aside>

      {/* Painel do formulário */}
      <main className={styles.formPane}>
        <div className={styles.formShell}>
          {/* Logo para mobile */}
          <div className={styles.mobileLogoBlock}>
            <img src={tatuLogo} alt="TATUDashboard" className={styles.mobileLogo} />
          </div>

          <div className={styles.formHeader}>
            <span className={styles.kicker}>
              <span className={styles.kickerDot} /> Acesso seguro
            </span>
            <h2 className={styles.formTitle}>Dashboard Produtos</h2>
            <p className={styles.formSub}>Faça login para acessar o sistema</p>
          </div>

          {status === 'success' ? (
            <SuccessState email={email} />
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <FormField
                label="E-mail"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                error={errors.email}
                icon={<Icon name="mail" />}
                placeholder="seu@email.com"
                disabled={status === 'loading'}
              />
              <FormField
                label="Senha"
                type={showPass ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                error={errors.password}
                icon={<Icon name="lock" />}
                placeholder="••••••••"
                disabled={status === 'loading'}
                rightSlot={
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPass(s => !s)}
                    aria-label="Mostrar/ocultar senha"
                  >
                    <Icon name={showPass ? 'eye-off' : 'eye'} size={18} />
                  </button>
                }
              />

              <div className={styles.formRow}>
                <label className={styles.check}>
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={e => setRemember(e.target.checked)}
                  />
                  <span className={`${styles.checkBox} ${remember ? styles.checkBoxChecked : ''}`}>
                    <Icon name="check" size={12} />
                  </span>
                  <span>Lembrar de mim</span>
                </label>
                <button type="button" className={styles.link}>Esqueci a senha</button>
              </div>

              {formError && (
                <div className={styles.formAlert}>{formError}</div>
              )}

              <button
                type="submit"
                className={`${styles.btnPrimary} ${status === 'loading' ? styles.btnLoading : ''}`}
                disabled={status === 'loading'}
              >
                <span className={styles.btnLabel}>
                  {status === 'loading' ? 'Entrando…' : 'Entrar'}
                </span>
                {status !== 'loading' && <Icon name="arrow" size={18} />}
              </button>

              {/* Link de registro removido por segurança - apenas admins podem criar usuários */}
            </form>
          )}

          <footer className={styles.formFoot}>
            <span>© 2026 TATUDashboard</span>
            <span className={styles.dot}>•</span>
            <span className={styles.linkQuiet}>Privacidade</span>
            <span className={styles.dot}>•</span>
            <span className={styles.linkQuiet}>Termos</span>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Login;
